import { getSmartEdge } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChart/SmartEdge/getSmartEdge/getSmartEdge.ts';
import { makeComplexDataTestId, TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { BaseEdge, BezierEdge, Edge, EdgeLabelRenderer, EdgeProps, Node } from '@xyflow/react';
import { useMemo } from 'react';
import styled from 'styled-components';
import type { SmartEdgeOptions } from './SmartEdge.types';

interface Props<EdgeDataType extends Edge = Edge, NodeDataType extends Node = Node>
	extends EdgeProps<EdgeDataType> {
	nodes: Node<NodeDataType>[];
	options: SmartEdgeOptions;
}

export function SmartEdge<EdgeDataType extends Edge = Edge, NodeDataType extends Node = Node>({
	nodes,
	options,
	...edgeProps
}: Props<EdgeDataType, NodeDataType>) {
	const {
		sourceX,
		sourceY,
		sourcePosition,
		targetX,
		targetY,
		targetPosition,
		style,
		label,
		labelStyle,
		markerEnd,
		markerStart,
		interactionWidth,
		source,
		target,
	} = edgeProps;
	const dataTestId =
		typeof label === 'string'
			? makeComplexDataTestId(TEST_ELEMENT_IDS.GSC_EDGE_LABEL, label)
			: TEST_ELEMENT_IDS.GSC_EDGE_LABEL;

	const FallbackEdge = options.fallback || BezierEdge;
	const isDragging = useMemo(
		() => !!nodes.find((n) => n.dragging && (n.id === source || n.id === target)),
		[nodes, source, target]
	);

	const smartResponse = useMemo(() => {
		return isDragging
			? null
			: getSmartEdge({
					sourcePosition,
					targetPosition,
					sourceX,
					sourceY,
					targetX,
					targetY,
					options: {
						...options,
						gridRatio: isDragging ? options.gridRatioDragging : options.gridRatio,
					},
					nodes,
				});
	}, [source, target, isDragging]);

	if (smartResponse === null) {
		return <FallbackEdge {...edgeProps} />;
	}

	const { edgeCenterX, edgeCenterY, svgPathString } = smartResponse;

	return (
		<>
			<BaseEdge
				path={svgPathString}
				style={style}
				markerStart={markerStart}
				markerEnd={markerEnd}
				interactionWidth={interactionWidth}
			/>
			<EdgeLabelRenderer>
				<StyledLabel
					style={{
						...labelStyle,
						transform: `translate(-50%, -50%) translate(${edgeCenterX}px,${edgeCenterY}px)`,
					}}
					className="edge-label"
					data-edge-id={edgeProps.id}
					data-testid={dataTestId}
				>
					{label}
				</StyledLabel>
			</EdgeLabelRenderer>
		</>
	);
}

const StyledLabel = styled.div`
	position: absolute;
	background-color: ${({ theme }) => theme.v3.colors.gray[0]};
	padding: 0.25rem;
	border-radius: 2px;
	border: 1px solid ${({ theme }) => theme.v3.colors.gray[4]};
	line-height: 1;
	color: ${({ theme }) => theme.v3.colors.dark[0]};
	z-index: 1004;
	pointer-events: all;

	&.hovered {
		z-index: 1005 !important;
		border-color: ${({ theme }) => theme.v3.colors.blue[6]};
		background-color: ${({ theme }) => theme.v3.colors.blue[0]};
		color: ${({ theme }) => theme.v3.colors.blue[6]};
		transition: stroke fill 0.5s ease-in-out;
	}
`;
